import { useEffect, useState, useRef, useCallback } from "react";
import { CalendarClock, Video, Clock, Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const TYPES = [
  "Free Introductory Discovery Call", "Paid HR Consultation", "Career Consultation",
  "Resume Intake Appointment", "Interview Coaching Session",
];

const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";

export default function BookConsultation() {
  const [settings, setSettings] = useState(null);
  const calendlyRef = useRef(null);

  const loadSettings = useCallback(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  useEffect(() => {
    const url = settings?.calendly_url;
    if (!url) return;

    if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_CSS;
      document.head.appendChild(link);
    }

    const render = () => {
      if (window.Calendly && calendlyRef.current) {
        calendlyRef.current.replaceChildren();
        window.Calendly.initInlineWidget({ url, parentElement: calendlyRef.current });
      }
    };

    if (window.Calendly) {
      render();
      return;
    }
    const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", render);
      return () => existing.removeEventListener("load", render);
    }
    const s = document.createElement("script");
    s.src = CALENDLY_SCRIPT;
    s.async = true;
    s.onload = render;
    document.body.appendChild(s);
  }, [settings]);

  return (
    <div data-testid="book-page">
      <SEO title="Book a Consultation" description="Schedule a free discovery call or a paid consultation with people, redefined. Automatic time zone handling and confirmations." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Book a Consultation</span>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso mt-5 leading-tight">Let's find a time.</h1>
            <p className="text-lg text-espresso/75 mt-6 leading-relaxed">
              Choose the conversation that fits your needs. Every session is virtual, with automatic
              time-zone handling, confirmations, and easy rescheduling.
            </p>
            <ul className="mt-8 space-y-3">
              {TYPES.map((t) => (
                <li key={t} className="flex gap-3 text-espresso/80"><Check size={18} className="text-champagne mt-1 shrink-0" />{t}</li>
              ))}
            </ul>
            <div className="grid grid-cols-3 gap-4 mt-10">
              {[{ i: Video, l: "100% virtual" }, { i: Clock, l: "Time-zone smart" }, { i: CalendarClock, l: "Easy reschedule" }].map((x) => (
                <div key={x.l} className="border border-champagne/35 rounded-2xl p-5 text-center">
                  <x.i className="mx-auto text-champagne" size={22} />
                  <p className="text-xs text-espresso/70 mt-2">{x.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="border border-champagne/35 rounded-3xl p-3 min-h-[640px]">
              {settings === null ? (
                <div className="h-[600px] flex items-center justify-center font-serif text-2xl text-espresso animate-pulse">Loading…</div>
              ) : settings.calendly_url ? (
                <div ref={calendlyRef} className="bg-ivory rounded-2xl overflow-hidden" style={{ minWidth: "320px", height: "640px" }} data-testid="calendly-embed" />
              ) : (
                <div data-testid="booking-not-connected" className="h-[620px] flex flex-col items-center justify-center text-center px-8 bg-ivory rounded-2xl">
                  <CalendarClock className="text-champagne" size={44} />
                  <h3 className="font-serif text-3xl text-espresso mt-5">Scheduling connects soon.</h3>
                  <p className="text-espresso/70 mt-3 max-w-md">
                    Live booking is powered by Calendly. The owner can connect a Calendly account in the
                    admin dashboard to display real availability here — we never show fictional time slots.
                  </p>
                  <a href="mailto:contact@peopleredefined.com" className="mt-8 bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors" data-testid="booking-email-fallback">
                    Request a time by email
                  </a>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
